import { useState } from 'react';

import {
  Text,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  Switch,
  useColorModeValue,
  Tooltip,
  RadioGroup,
  Radio,
  Collapse,
} from '@ecoinvent/ui';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { IoOptions } from 'react-icons/io5';

import sideBySideDark from 'assets/side-by-side-dark.svg';
import sideBySideLight from 'assets/side-by-side.svg';
import verticalDark from 'assets/vertical-dark.svg';
import verticalLight from 'assets/vertical.svg';

type Props = {
  showComments: boolean;
  setShowComments: (showComments: boolean) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  isHorizontalLayout: boolean;
  setIsHorizontalLayout: (isHorizontalLayout: boolean) => void;
  viewOptionDirection: string[];
  setViewOptionDirection: (viewOptionDirection: string[]) => void;
  viewOption: string[];
  setViewOption: (viewOption: string[]) => void;
};

const ViewOptionsMenu = ({
  showComments,
  setShowComments,
  sortBy,
  setSortBy,
  isHorizontalLayout,
  setIsHorizontalLayout,
  viewOptionDirection,
  setViewOptionDirection,
  viewOption,
  setViewOption,
}: Props) => {
  const [openSortBy, setOpenSortBy] = useState(false);
  const handleViewOptionChange = (option: string) => {
    if (viewOption.includes(option)) {
      setViewOption(viewOption.filter((item) => item !== option));
    } else {
      setViewOption([...viewOption, option]);
    }
  };

  const handleViewOptionDirectionChange = (option: string) => {
    if (viewOptionDirection.includes(option)) {
      setViewOptionDirection(viewOptionDirection.filter((item) => item !== option));
    } else {
      setViewOptionDirection([...viewOptionDirection, option]);
    }
  };

  const menuBg = useColorModeValue('white', 'gray.800');
  const menuButtonHoverColor = useColorModeValue('gray.200', 'gray.700');
  const sideBySide = useColorModeValue(sideBySideLight, sideBySideDark);
  const vertical = useColorModeValue(verticalLight, verticalDark);

  return (
    <Menu autoSelect={false} placement="bottom-start">
      <Tooltip label="View options" hasArrow placement="top">
        <MenuButton
          px={3}
          py={2}
          transition="all 0.2s"
          borderRadius="md"
          borderWidth="1px"
          _hover={{ bg: menuButtonHoverColor }}
        >
          <IoOptions />
        </MenuButton>
      </Tooltip>
      <MenuList bg={menuBg} mr={5}>
        <>
          <Text fontWeight={700} fontSize="lg" px={3} py={3}>
            View options
          </Text>
          <Stack flexDir="row" spacing={4} p={3} alignItems="center" justifyContent="space-between">
            <Text whiteSpace="nowrap">Show comments</Text>
            <Switch isChecked={showComments} colorScheme="green" size="md" onChange={() => setShowComments(!showComments)} />
          </Stack>
          <MenuDivider />
          <Stack p={3}>
            <Stack
              flexDir="row"
              spacing={4}
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setOpenSortBy(!openSortBy)}
              cursor="pointer"
              role="button"
              tabIndex={0}
            >
              <Text>Sort by</Text>
              <Icon as={openSortBy ? BiChevronUp : BiChevronDown} />
            </Stack>
            <Collapse in={openSortBy}>
              <RadioGroup onChange={setSortBy} value={sortBy}>
                <Stack direction="column" px={3} py={2}>
                  <Radio colorScheme="green" value="alphabetical-az">
                    Alphabetical (A to Z)
                  </Radio>
                  <Radio colorScheme="green" value="alphabetical-za">
                    Alphabetical (Z to A)
                  </Radio>
                  <Radio colorScheme="green" value="highest-amount">
                    Highest amount first
                  </Radio>
                  <Radio colorScheme="green" value="lowest-amount">
                    Lowest amount first
                  </Radio>
                </Stack>
              </RadioGroup>
            </Collapse>
          </Stack>
          <MenuDivider />
          <Text px={3} py={1} sx={{ color: 'gray.500', fontSize: 'sm' }}>
            View as
          </Text>
          <Stack direction="row" p={3}>
            <MenuItem onClick={() => setIsHorizontalLayout(true)} w="auto" borderRadius="md">
              <Stack direction="column" alignItems="center" p={0}>
                <Image src={sideBySide} alt="side by side view" w="138px" />
                <Text>Side-by-side</Text>
              </Stack>
            </MenuItem>
            <MenuItem onClick={() => setIsHorizontalLayout(false)} w="auto" borderRadius="md">
              <Stack direction="column" alignItems="center" p={0}>
                <Image src={vertical} alt="vertical view" w="138px" />
                <Text>Vertical</Text>
              </Stack>
            </MenuItem>
          </Stack>
          {!isHorizontalLayout && (
            <Stack>
              <MenuDivider />
              <Text px={3} py={1} sx={{ color: 'gray.500', fontSize: 'sm' }}>
                Show direction
              </Text>
              <Stack px={3} py={2} spacing={3}>
                <Stack flexDir="row" alignItems="center" justifyContent="space-between">
                  <Text>Inputs</Text>
                  <Switch
                    colorScheme="green"
                    size="md"
                    isChecked={viewOptionDirection.includes('inputs')}
                    onChange={() => handleViewOptionDirectionChange('inputs')}
                  />
                </Stack>

                <Stack flexDir="row" alignItems="center" justifyContent="space-between">
                  <Text>Outputs</Text>
                  <Switch
                    colorScheme="green"
                    size="md"
                    isChecked={viewOptionDirection.includes('outputs')}
                    onChange={() => handleViewOptionDirectionChange('outputs')}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
          <MenuDivider />
          <Text px={3} py={1} sx={{ color: 'gray.500', fontSize: 'sm' }}>
            Show in table
          </Text>
          <Stack px={3} py={2} spacing={3}>
            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Reference flow</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('reference flow')}
                onChange={() => handleViewOptionChange('reference flow')}
              />
            </Stack>

            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Valuable</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('valuable')}
                onChange={() => handleViewOptionChange('valuable')}
              />
            </Stack>

            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Waste</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('waste')}
                onChange={() => handleViewOptionChange('waste')}
              />
            </Stack>

            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Byproduct</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('byproduct')}
                onChange={() => handleViewOptionChange('byproduct')}
              />
            </Stack>

            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Emission</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('emission')}
                onChange={() => handleViewOptionChange('emission')}
              />
            </Stack>

            <Stack flexDir="row" alignItems="center" justifyContent="space-between">
              <Text>Resource</Text>
              <Switch
                colorScheme="green"
                size="md"
                isChecked={viewOption.includes('resource')}
                onChange={() => handleViewOptionChange('resource')}
              />
            </Stack>
          </Stack>
        </>
      </MenuList>
    </Menu>
  );
};

export default ViewOptionsMenu;
